import { TimeMs } from '../types';

const pad = (n: number, width = 2): string => n.toString().padStart(width, '0');

/**
 * Available sub-second precisions for the playhead monitor.
 *   - 'mm-ss':         00:12       — updates 1×/s. Most readable.
 *   - 'mm-ss-tenths':  00:12.5     — 10×/s. Decimal style.
 *   - 'mm-ss-frames':  00:12:15    — fps×/s. SMPTE-style timecode.
 *   - 'mm-ss-cents':   00:12:45    — 100×/s. Most precise, hardest to read.
 */
export type TimecodeFormat = 'mm-ss' | 'mm-ss-tenths' | 'mm-ss-frames' | 'mm-ss-cents';

export const TIMECODE_FORMAT_LABELS: Record<TimecodeFormat, string> = {
  'mm-ss': 'MM:SS',
  'mm-ss-tenths': 'MM:SS.d',
  'mm-ss-frames': 'MM:SS:FF',
  'mm-ss-cents': 'MM:SS:cc'
};

/**
 * Coarse `MM:SS` formatter. Used in the ruler ticks where sub-second
 * resolution would be noise (ticks always land on whole seconds anyway).
 */
export const formatTime = (ms: TimeMs): string => {
  const totalSec = Math.floor(Math.max(0, ms) / 1000);
  return `${pad(Math.floor(totalSec / 60))}:${pad(totalSec % 60)}`;
};

/**
 * Configurable timecode formatter for the playhead monitor.
 */
export const formatTimecode = (ms: TimeMs, format: TimecodeFormat, fps = 30): string => {
  const safe = Math.max(0, ms);
  const totalSec = Math.floor(safe / 1000);
  const m = pad(Math.floor(totalSec / 60));
  const s = pad(totalSec % 60);
  const subMs = safe % 1000;

  switch (format) {
    case 'mm-ss':
      return `${m}:${s}`;
    case 'mm-ss-tenths':
      return `${m}:${s}.${Math.floor(subMs / 100)}`;
    case 'mm-ss-frames':
      return `${m}:${s}:${pad(Math.floor((subMs / 1000) * fps))}`;
    case 'mm-ss-cents':
      return `${m}:${s}:${pad(Math.floor(subMs / 10))}`;
  }
};
